<template>
  <section v-if="relationTag" class="relation-mention-tag">
    <div
        :id="parentContainerId+'-'+stringToClass(tag.name)"
        :class="{
          tag: true,
          [tag.category]: tag.category,
          'tag-with-sentiment': tag.show_sentiment,
          ['tag-parent-'+tag.parent]: tag.parent,
          'tag-with-delete': !isOrganizationTag,
          'tag-with-relation': tagAllowsRelationship,
          'relation-tag': relationTag
         }"
        :tag-data="stringToClass(tag.name)"
        :tag-grouping="tag.category"
        :style="{background: tag.color}" >

      <span>{{ tag.name }} </span>
      <div class="sentiment-wrapper" v-if="tag.show_sentiment">
        <span class="sentiment positive align-center"
            v-on:click="(e) => onSentimentClick(e, tag, true)">
          {{ getTagScore(tag, true) }}
        </span>
        <span class="sentiment negative align-center"
            v-on:click="(e) => onSentimentClick(e, tag, false)">
          {{ getTagScore(tag, false) }}
        </span>
      </div>
      <span v-if="tagAllowsRelationship"
            class="fas fa-link"
            v-on:click="(e) => onRelationTagArrowClick(e, tag)" />
      <div class="delete-tag fas fa-times"
           v-if="!isOrganizationTag"
           @click.prevent.stop="deleteMentionTag(tag)" />

    </div>
    <app-relation-mention-tag
          :loading.sync="loading"
          :parentContainerId="parentContainerId+'-'+stringToClass(tag.name)"
          :tag="tag"
          :relationTag="relationTag"
          :activeOrganization="activeOrganization"
          :showScoringSystem="showScoringSystem"
          :keysPressed="keysPressed"
          @deleteRelationMentionTag="$emit('deleteRelationMentionTag', $event)"
          @updateMentionSentiment="$emit('updateMentionSentiment', $event)"
          @recordAction="$emit('recordAction', $event)" />
  </section>
  <div v-else class="tag with-category-wrapper d-inline-block">
    <span v-if="isUseOrgWithCategory" :id="parentContainerId+'-'+stringToClass(tag.name)">
      <span 
        v-if="categoryName && firstTagMention" 
        :id="'result-'+stringToClass(categoryTag.name)+'-'+stringToClass(categoryTag.name)+'-'+categoryName"
        :class="'tag bg-secondary cat-'+stringToClass(categoryTag.name)"
        :category-key="categoryTag.key"
      >
        <span>{{ firstTagMention }}</span>
      </span> 
    </span>
    <div :id="parentContainerId+'-'+stringToClass(tag.name)"
        :class="{
          tag: true,
          [tag.category]: tag.category,
          'tag-with-sentiment': tag.show_sentiment,
          ['tag-parent-'+tag.parent]: tag.parent,
          'tag-with-delete': !isOrganizationTag,
          'tag-with-relation': tagAllowsRelationship,
          'relation-tag': relationTag
        }"
        :tag-data="stringToClass(tag.name)"
        :tag-grouping="tag.category"
        :style="{background: tag.is_private || (tag.tag_category && tag.tag_category.is_private_insight) ? 'orange' : tag.color}"
        v-on:mouseleave="resetQuoteIndex">
      <div class="tag-content">
        <span>{{ tagName }}</span>

        <span @click="clickTagComments">
          <v-popover 
            :disabled="!isSourceTag || (tag.mentions && tag.mentions.length <= 0)"
            trigger="click"
          >
            <span
              v-if="tag.mentions && tag.mentions.length"
              role="button" 
              class="mentions-tag"
              aria-pressed="true"
            >
              <i class="fa fa-comment"></i>
              {{ tag.mentions.length }}
            </span>

            <template slot="popover">
              <div class="mention-pagination">
                <small v-if="tag.mentions && tag.mentions.length > 1">
                  {{ quoteIndex+1 }} / {{ tag.mentions.length }}
                </small>
              </div>
              <div
                v-if="isSourceTag && tag.mentions && tag.mentions.length > 0"
                :key="`mention-${quoteIndex}`"
                :class="{ 'quote-carousel': true, 'with-arrows': tag.mentions.length > 1 }"
              >
                <div class="quote-span">
                  <span>{{ tag.mentions[quoteIndex].quote }}</span>
                </div>

                <div v-if="tag.mentions && tag.mentions.length > 1" class="carousel-arrows">
                  <span
                    class="fas fa-angle-up arrow arrow-up"
                    :class="{ disabled: quoteIndex+1 <= 1 }"
                    @click="previousQuote(tag)"
                  />
                  <span
                    class="fas fa-angle-down arrow arrow-down"
                    :class="{ disabled: quoteIndex+1 >= tag.mentions.length }"
                    @click="nextQuote(tag)"
                  />
                </div>
              </div>
            </template>
          </v-popover>
        </span>

        <span class="sentiment-wrapper" v-if="tag.show_sentiment">
          <span
            class="sentiment positive align-center"
            @click="(e) => onSentimentClick(e, tag, true)"
          >
            {{ getTagScore(tag, true) }}
          </span>
          <span
            class="sentiment negative align-center"
            @click="(e) => onSentimentClick(e, tag, false)"
          >
            {{ getTagScore(tag, false) }}
          </span>
        </span>

        <span
          v-if="tagAllowsRelationship"
          class="fas fa-link"
          @click="(e) => onRelationTagArrowClick(e, tag)"
        ></span>
        <span
          class="delete-tag fas fa-times"
          v-if="!isOrganizationTag"
          @click.prevent.stop="deleteMentionTag"
        ></span>
      </div>

    </div>
  </div>
</template>

<script>
  import { mapState } from 'vuex';
  import _ from 'lodash';

  import RelationMentionTag from './relation_mention_tag';

  import editor from '../../helpers/editor';
  import array from '../../helpers/array';
  import { TAG_CATEGORY } from '../../config/const';
  import SHORTCUT from '../../config/shortcut-keys';

  export default {
    props: {
      parentContainerId: String,
      tag: Object,
      relationTag: {
        type: Object,
        default: undefined
      },
      organization: {
        type: Object,
        required: false,
        default: () => {
          return {};
        }
      },
      activeOrganization: String,
      keysPressed: {
        type: Array,
        required: true
      },
      loading: {
        type: Boolean,
        required: false,
        default: false
      },
      mentions: Array,
      categoryMentionKeys: Array
    },
    components: {
      'app-relation-mention-tag': RelationMentionTag
    },
    computed: {
      ...mapState('tags', [
        'tags'
      ]),
      ...mapState('editorProjectSettings', [
        'editorProjectSettings'
      ]),
      ...mapState('briefMappings', [
        'briefMappings'
      ]),
      ...mapState({
        projectSetupGeneral: state => state?.projectSetup?.projectSetup?.data?.general
      }),
      isOrganizationTag () {
        return this.tag.category === TAG_CATEGORY.ORGANIZATION;
      },
      isSourceTag () {
        return this.tag.category === TAG_CATEGORY.SOURCES && this.tag.id;
      },
      relationTagsMenu () {
        if(this.tags.relationshipData && !_.isUndefined(this.tags.relationshipData.data) && Object.keys(this.tags.relationshipData.data).length > 0) {
          return Object.values(this.tags.relationshipData.data).filter(tag => tag.key != this.tag.key)
        }
        return undefined;
      },
      tagAllowsRelationship () {
        if(this.isUseOrgWithCategory) return !this.isOrganizationTag && this.tag.allow_relationship;
        return !this.isOrganizationTag && this.tag.allow_relationship && !!this.relationTagsMenu && Object.keys(this.relationTagsMenu).length > 0
      },
      showScoringSystem () {
        return this.editorProjectSettings.data && this.editorProjectSettings.data.scoring_system && this.editorProjectSettings.data.scoring_system.toLowerCase() !== "none" ? true: false;
      },
      tagName () {
        let name = this.tag.name;
        if (this.tag.hasOwnProperty('active') && !this.tag.active) {
          name = name + " (Inactive)";
        }
        return name;
      },
      quoteIndexLength () {
        return this.isSourceTag && this.tag.mentions && this.tag.mentions.length > 0 ? this.tag.mentions.length : -1;
      },
      clonedMentions () {
        let _copy = _.cloneDeep(this.mentions);
        if(!this.isUseOrgWithCategory) return _copy;
        let parentIndex = _copy.findIndex(cm => cm?.id === this.tag?.parent_tag_id);
        if (parentIndex > 0) {
          // Swap the first element with the parent element
          let temp = _copy[0];
          _copy[0] = _copy[parentIndex];
          _copy[parentIndex] = temp;
        }
        return _copy;
      },
      categoryKey () {
        return (this.mentions || []).find(dt => dt.tag_category.is_organization_category)?.key;
      },
      categoryTag () {
        if(this.categoryKey === TAG_CATEGORY.SOURCES) {
          return { key: TAG_CATEGORY.SOURCES, name: 'Sources' };
        }
        else {
          return this.briefMappings && this.briefMappings.data && this.briefMappings.data.data ?
            this.briefMappings.data.data[this.categoryKey] :
            {};
        }
      },
      categoryName () {
        let name = this.categoryTag && this.categoryTag.name;
        if (this.categoryTag && this.categoryTag.hasOwnProperty('active') && !this.categoryTag.active) {
          name = name + " (Inactive)";
        }
        return name;
      },
      firstTagMention(){
        return this.clonedMentions?.find(cm => this?.tag?.parent_tag_id === cm?.id)?.name || '';
      },
      isUseOrgWithCategory() { return this.projectSetupGeneral?.organizations_with_category }
    },
    watch: {
      quoteIndexLength: {
        handler (newValue, oldValue) {
          //reset quote index when mentions length changes
          if(newValue !== oldValue) {
            this.resetQuoteIndex();
          }
        }
      }
    },
    methods: {
      clickTagComments(e) {
        e.preventDefault();
        e.stopPropagation();
      },
      stringToClass(string) {
        return editor.stringToClass(string);
      },
      onSentimentClick(e, tag, isPositive) {
        e.preventDefault();
        e.stopPropagation();
        let reducePoints = e.shiftKey ? true : false;

        //log mentions before modification of sentiment
        this.$emit('recordAction', tag);
        this.$emit('updateMentionSentiment', {tag: tag, isPositive: isPositive, isReduce: reducePoints});
      },
      onRelationTagArrowClick(e, tag) {
        e.preventDefault();
        e.stopPropagation();

        const clickPosition = {
          xCoordinate: e.clientX,
          yCoordinate: e.clientY
        };

        this.$emit('showRelationTagMenu', {tag: tag, clickPosition: clickPosition});
      },
      getTagScore (tag, positive) {
        return editor.getTagScore(tag, positive);
      },
      deleteMentionTag() {
        //log mentions before removing tag
        this.$emit('recordAction', this.tag);
        this.$emit('deleteMentionTag', { tag: this.tag, organization: this.organization });
      },
      previousQuote (tag) {
        this.quoteIndex = this.quoteIndex - 1 < 0 ? tag.mentions.length -1 : this.quoteIndex - 1;
      },
      nextQuote (tag) {
        this.quoteIndex = this.quoteIndex + 1 > tag.mentions.length -1 ? 0 : this.quoteIndex + 1;
      },
      resetQuoteIndex () {
        this.quoteIndex = 0;
      }
    },
    data () {
      return {
        quoteIndex: 0
      }
    }
  }
</script>

<style lang="scss">
  .tag-content {
    display: flex;

    .mentions-tag {
      padding: 0px 3px 0 10px;
      cursor: pointer;
    }
  }

  .mention-pagination {
    padding: 0px;
    text-align: right;
    opacity: 0.6;
  }

  .arrow {
    cursor: pointer;
  }
</style>